import classNames from 'classnames';
import React from 'react';
import Select from 'react-select';
import { FiPlus } from 'react-icons/fi';
import { SortableContainer, SortableElement } from 'react-sortable-hoc';

import {
    SidebarHeader,
    ScrollableBox,
    Spinner,
    Textarea,
    Checkbox,
    Button,
    FormField,
    CancelButton,
    NavigationGuard,
    CopyLink,
    Feature,
} from '@mssgme/ui';
import { PageLayout } from '../../../../UI';
import { OldSaveButton, ProductPriceInputs } from '../UI';
import { useProductEdit, ACCEPT_IMAGES } from './useProductEdit';

import styles from './ProductEdit.scss';

const SortableItem = SortableElement(({ url, removePhoto }) => (
    <li className={styles.imagePreview}>
        <div className={styles.imagePreviewContainer}>
            <img src={url} alt="" />
        </div>
        <div className={styles.removePhotoBtn} onClick={removePhoto(url)} data-id="remBtn">
            <FiPlus size={12} />
        </div>
    </li>
));

const SortableList = SortableContainer(({ photos, removePhoto, isUploading, isFetching, handlePhotoChange }) => {
    return (
        <ul className={styles.imagesList}>
            {photos.map((url, index) => (
                <SortableItem key={url} index={index} url={url} removePhoto={removePhoto} />
            ))}
            {(!photos || photos.length < 6) && (
                <li className={styles.imagePreview}>
                    <div className={styles.addPhotoBtn}>
                        <div className={styles.addPhotoBtnContent}>
                            {!isUploading && <FiPlus size={26} />}
                            {isUploading && <Spinner />}
                        </div>
                        {!isUploading && (
                            <input
                                type="file"
                                name="photos"
                                multiple
                                onChange={handlePhotoChange}
                                disabled={isFetching}
                                className={styles.addPhotoBtnInput}
                                accept={ACCEPT_IMAGES.join(',')}
                            />
                        )}
                    </div>
                </li>
            )}
        </ul>
    );
});

export default function ProductEdit() {
    const {
        t,
        hasChanges,
        isNew,
        isFetching,
        isLoadingProduct,
        handleSubmit,
        product,
        handleChange,
        baseurl,
        handlePhotoChange,
        removePhoto,
        isUploading,
        onSortEnd,
        errors,
        hasPrice,
        defaultCurrency,
        refAmount,
        collections,
        collectionsOptions,
        selectThemeOverride,
        selectStyles,
        handleCollectionChange,
        hasExternalUrl,
        toggleLink,
        handleRemoveClick,
        history,
    } = useProductEdit();

    return (
        <NavigationGuard blocked={hasChanges}>
            <PageLayout>
                <SidebarHeader
                    heading={t(isNew ? 'productEdit.headerTitle' : 'productEdit.headerEditTitle')}
                    leftButton={<CancelButton tag="span" onClick={() => history.goBack()} styled="backIcon" />}
                    rightButton={
                        hasChanges && (
                            <OldSaveButton isFetching={isFetching && !isLoadingProduct} onClick={handleSubmit} />
                        )
                    }
                />
                <ScrollableBox>
                    {isLoadingProduct ? (
                        <Spinner />
                    ) : (
                        <Feature rule="products:edit" subject={product}>
                            <form noValidate onSubmit={handleSubmit}>
                                {!isNew && (
                                    <div className={styles.header}>
                                        <div className={styles.publishedContainer}>
                                            <div className={styles.publishedLabel}>
                                                <span
                                                    className={classNames(
                                                        styles.publishedDot,
                                                        product.published ? styles.green : null
                                                    )}
                                                />
                                                <span>
                                                    {product.published
                                                        ? t('productEdit.active')
                                                        : t('productEdit.inactive')}
                                                </span>
                                            </div>
                                            <div>
                                                <Checkbox
                                                    checked={product.published}
                                                    name="published"
                                                    onChange={handleChange}
                                                    disabled={isFetching}
                                                    styled="toggle"
                                                />
                                            </div>
                                        </div>
                                        {product.published && (
                                            <CopyLink
                                                legacy
                                                tag="a"
                                                className={styles.userLink}
                                                href={`${baseurl}/p/${product.slug}`}
                                            />
                                        )}
                                    </div>
                                )}
                                <div className={styles.photosContainer}>
                                    <span className={styles.sectionLabel}>{t('productEdit.photo')}</span>
                                    <div className={styles.imageUploadContainer}>
                                        <div className={styles.imagesBlock}>
                                            {product.photos && (
                                                <SortableList
                                                    photos={product.photos}
                                                    handlePhotoChange={handlePhotoChange}
                                                    removePhoto={removePhoto}
                                                    axis="xy"
                                                    isUploading={isUploading}
                                                    isFetching={isFetching}
                                                    onSortEnd={onSortEnd}
                                                    helperClass={styles.sortable}
                                                    shouldCancelStart={(e) => {
                                                        if (e.target.dataset.id === 'remBtn') {
                                                            return true;
                                                        }
                                                    }}
                                                />
                                            )}
                                        </div>
                                    </div>
                                    {errors.photos && <span className={styles.photosError}>{errors.photos}</span>}
                                </div>
                                <FormField
                                    label={t('productEdit.title')}
                                    id="product_name"
                                    name="name"
                                    value={product.name}
                                    onChange={handleChange}
                                    errors={errors.name}
                                    maxLength={100}
                                    disabled={isFetching}
                                />
                                <div className={classNames(styles.publishedContainer, styles.priceTogglerContainer)}>
                                    <div className={classNames(styles.publishedLabel, styles.priceLabel)}>
                                        <span>{t('productEdit.price')}</span>
                                    </div>
                                </div>
                                {hasPrice && (
                                    <ProductPriceInputs
                                        name="price"
                                        value={{ currency: defaultCurrency, ...product.price }}
                                        onChange={handleChange}
                                        errors={errors.price}
                                        refAmount={refAmount}
                                    />
                                )}
                                <FormField
                                    component={Textarea}
                                    label={t('productEdit.description')}
                                    id="product_description"
                                    name="description"
                                    value={product.description}
                                    onChange={handleChange}
                                    errors={errors.description}
                                    maxLength={10000}
                                    disabled={isFetching}
                                />
                                {collections.length > 0 && (
                                    <FormField
                                        component={Select}
                                        theme={selectThemeOverride}
                                        styles={selectStyles}
                                        isMulti
                                        isClearable={false}
                                        placeholder={t('PageProductCollections.noCollection')}
                                        options={collectionsOptions}
                                        label={t('collectionEdit.headerEditTitle')}
                                        name="col"
                                        value={product.cols}
                                        onChange={handleCollectionChange}
                                        errors={errors.cols}
                                        disabled={isFetching}
                                        isSearchable={false}
                                        noOptionsMessage={() => t('productEdit.noCategories')}
                                    />
                                )}
                                <div className={classNames(styles.publishedContainer, styles.priceTogglerContainer)}>
                                    <div className={classNames(styles.publishedLabel, styles.priceLabel)}>
                                        <span>{t('productEdit.urlToggleLabel')}</span>
                                    </div>
                                    <div>
                                        <Checkbox
                                            checked={hasExternalUrl}
                                            onChange={toggleLink}
                                            disabled={isFetching}
                                            styled="toggle"
                                        />
                                    </div>
                                </div>
                                {hasExternalUrl && (
                                    <>
                                        <FormField
                                            id="product_externalUrlTitle"
                                            name="externalButton.title"
                                            value={product.externalButton.title}
                                            onChange={handleChange}
                                            errors={errors.externalButton?.title}
                                            maxLength={50}
                                            disabled={isFetching}
                                            placeholder={t('productEdit.urlTitlePlaceholder')}
                                        />
                                        <FormField
                                            id="product_externalUrl"
                                            name="externalButton.url"
                                            value={product.externalButton.url}
                                            onChange={handleChange}
                                            errors={errors.externalButton?.url}
                                            maxLength={500}
                                            disabled={isFetching}
                                            placeholder={t('productEdit.urlPlaceholder')}
                                        />
                                    </>
                                )}
                                {!isNew && (
                                    <div className={styles.removeBtnContainer}>
                                        <Button type="button" onClick={handleRemoveClick} btnStyle="danger">
                                            {t('button.remove')}
                                        </Button>
                                    </div>
                                )}
                            </form>
                        </Feature>
                    )}
                </ScrollableBox>
            </PageLayout>
        </NavigationGuard>
    );
}
